
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { BankObject } from '@/store/modules/Members/BanksModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'banks-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('banksUpdating'), [
          translate('appraisals'),
        ]);
      });

      const { data } = await store.dispatch(Actions.GET_BANK, route.params.id);

      const bank = ref<BankObject>(data.data);
      const email = bank.value.email;
      const bankName = bank.value.name;

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const bankSchema = Yup.object().shape({
        titleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        titleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
        name: Yup.string().label('English Name'),
        password: Yup.string()
          .min(4, () => translate('PASSWORD_MIN_4'))
          .max(20, () => translate('PASSWORD_MAX_20'))
          .matches(/^(?=.*[a-z])/, translate('PASSWORD_AT_LEAST_ONE_LOWER'))
          .matches(/^(?=.*[A-Z])/, translate('PASSWORD_AT_LEAST_ONE_UPPER'))
          .matches(/^(?=.*\d)/, translate('PASSWORD_AT_LEAST_ONE_NUMBER'))
          .matches(
            /^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/,
            translate('PASSWORD_AT_LEAST_ONE_SPECIAL')
          )
          .label('Password'),
        verifyPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], () =>
            translate('PASSWORDS_MUST_MATCH')
          )
          .label('Verify Password'),
        email: Yup.string()
          .email(() => translate('EMAIL_NOT_VALID'))
          .label('Email'),
      });

      const onSubmitUpdate = async (values) => {
        delete values.verifyPassword;
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await store.dispatch(Actions.UPDATE_BANK, {
          id: bank.value?.id,
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_BANK'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'banks-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        bankSchema,
        goBack,
        bank,
        can,
        email,
        bankName,
      };
    },
  });
